/**
 * PageInfoDrawer Component
 * Renders a small help IconButton that opens a right-side Drawer with
 * contextual documentation for a page.
 *
 * Consumers pass the page `title` and the explanatory content as `children`.
 * The open/close state is managed internally — consuming pages don't need
 * their own state hooks.
 */
import React, { useState } from 'react';
import {
  Box,
  Drawer,
  IconButton,
  Typography,
} from '@mui/material';
import {
  HelpOutline as HelpOutlineIcon,
  Close as CloseIcon,
} from '@mui/icons-material';

export interface PageInfoDrawerProps {
  /** Title shown in the drawer header (typically matches the page title) */
  title: string;
  /** Explanatory content rendered inside the drawer body */
  children: React.ReactNode;
  /** ARIA label for the trigger button. Defaults to `Mais informações sobre ${title}` */
  buttonAriaLabel?: string;
}

/**
 * PageInfoDrawer - Contextual help drawer anchored to the right edge.
 *
 * @example
 * <PageInfoDrawer title="Certificados Digitais">
 *   <Typography variant="subtitle1">O que é?</Typography>
 *   <Typography variant="body2">...</Typography>
 * </PageInfoDrawer>
 */
export const PageInfoDrawer: React.FC<PageInfoDrawerProps> = ({
  title,
  children,
  buttonAriaLabel,
}) => {
  const [open, setOpen] = useState(false);
  const ariaLabel = buttonAriaLabel ?? `Mais informações sobre ${title}`;

  return (
    <>
      <IconButton
        size='small'
        aria-label={ariaLabel}
        onClick={() => setOpen(true)}
      >
        <HelpOutlineIcon fontSize='small' color='action' />
      </IconButton>

      <Drawer
        anchor='right'
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 400 },
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 3,
            py: 2,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant='h6' component='h2'>
            {title}
          </Typography>
          <IconButton
            size='small'
            aria-label='Fechar'
            onClick={() => setOpen(false)}
          >
            <CloseIcon fontSize='small' />
          </IconButton>
        </Box>

        <Box sx={{ p: 3, overflowY: 'auto' }}>{children}</Box>
      </Drawer>
    </>
  );
};

